import {
  ArrowDown,
  ArrowRight,
  CalendarDays,
  Check,
  Plus,
  ShieldCheck,
  Sparkles,
  Truck,
} from 'lucide-react'
import { motion } from 'motion/react'
import { useMemo, useState } from 'react'
import {
  PROPOSED_DELIVERY_SLOTS,
  nextEligibleOccurrence,
} from '../../data/delivery'
import { products } from '../../data/products'
import { formatCurrency } from '../../lib/money'
import type { DeliveryChoice, Product } from '../../types'
import { DeliveryMap } from '../DeliveryMap'
import { CampaignImpactMedia } from '../media/LuminaPortalVideo'

interface TreatHeroProps {
  readonly onBrowse: () => void
}

export function TreatHero({ onBrowse }: TreatHeroProps) {
  return (
    <section
      className="impact-hero"
      data-shop-section="hero"
      data-treat-show-hero
    >
      <div className="impact-hero__copy" data-treat-show-hero-copy>
        <p className="impact-kicker" data-treat-show-intro>
          Capricornia&apos;s freeze-dried treat studio
        </p>
        <h1 data-treat-show-intro>
          <span>A little freeze.</span>
          <span>A lot of joy.</span>
        </h1>
        <p className="impact-hero__lede" data-treat-show-intro>
          Choose your favourites, let the Sub Zero team shape the surprise,
          then set a one-off, weekly or fortnightly local drop.
        </p>
        <div className="impact-hero__actions" data-treat-show-intro>
          <a className="impact-button impact-button--raspberry" href="#create-a-bucket">
            Build my bucket
            <ArrowRight aria-hidden="true" />
          </a>
          <button className="impact-text-button" type="button" onClick={onBrowse}>
            Shop treats
            <ArrowDown aria-hidden="true" />
          </button>
        </div>
        <p className="impact-demo-note" data-treat-show-intro>
          Interactive concept &mdash; nothing here places an order.
        </p>
      </div>

      <div
        className="impact-hero__visual"
        aria-label="Colourful treats bursting around a cream Sub Zero bucket"
      >
        <div className="impact-hero__film">
          <CampaignImpactMedia />
        </div>
        <p className="impact-hero__scene-caption">
          One bucket. Your mix.
          <span>Built for the proposed local drop.</span>
        </p>
      </div>

      <a className="impact-hero__scroll" href="#postcode-promise">
        See the local drop
        <ArrowDown aria-hidden="true" />
      </a>
    </section>
  )
}

type PostcodeStatus = 'idle' | 'unconfirmed' | 'invalid'

export function PostcodePromiseBand() {
  const [postcode, setPostcode] = useState('')
  const [status, setStatus] = useState<PostcodeStatus>('idle')

  function checkPostcode() {
    if (!/^\d{4}$/.test(postcode)) {
      setStatus('invalid')
      return
    }
    setStatus('unconfirmed')
  }

  return (
    <section
      className="impact-postcode"
      id="postcode-promise"
      data-shop-section="postcode-promise"
    >
      <div className="impact-postcode__promise">
        <span className="impact-postcode__icon">
          <Truck aria-hidden="true" />
        </span>
        <div>
          <p>Proposed local delivery pilot</p>
          <h2>A smarter local drop. More of the sale can stay local.</h2>
        </div>
      </div>
      <form
        className="impact-postcode__form"
        onSubmit={(event) => {
          event.preventDefault()
          checkPostcode()
        }}
      >
        <label htmlFor="impact-postcode-input">Check the pilot area</label>
        <div>
          <input
            id="impact-postcode-input"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={4}
            value={postcode}
            placeholder="Your postcode"
            onChange={(event) => {
              setPostcode(event.currentTarget.value.replace(/\D/g, ''))
              setStatus('idle')
            }}
          />
          <button type="submit">Check</button>
        </div>
        <p className={`impact-postcode__status is-${status}`} aria-live="polite">
          {status === 'idle' &&
            'Enter a postcode to preview how the proposed pilot check could work.'}
          {status === 'unconfirmed' &&
            'Noted for the proposed pilot. Sub Zero would confirm the launch map and route before opening orders.'}
          {status === 'invalid' && 'Enter a valid four-digit postcode.'}
        </p>
      </form>
    </section>
  )
}

interface StarsOfShowProps {
  readonly onAdd: (product: Product) => void
  readonly onQuickShop: (product: Product) => void
  readonly onViewCart: () => void
  readonly productQuantities: Readonly<Record<string, number>>
}

export function StarsOfShow({
  onAdd,
  onQuickShop,
  onViewCart,
  productQuantities,
}: StarsOfShowProps) {
  return (
    <section
      className="impact-products"
      id="stars-of-the-show"
      data-shop-section="stars-of-the-show"
    >
      <div className="impact-section-heading">
        <div>
          <p>Pick a hero</p>
          <h2>The crowd pleasers.</h2>
        </div>
        <p>
          Real Sub Zero favourites, ready to add now &mdash; or let the Sub Zero
          team shape a whole personalised bucket.
        </p>
      </div>

      <div className="impact-product-rail" aria-label="Sub Zero favourites">
        {products.map((product, index) => {
          const quantityInCart = productQuantities[product.id] ?? 0

          return (
            <motion.article
              className={`impact-product impact-product--${(index % 4) + 1}`}
              key={product.id}
              whileHover={{ y: -2 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <button
                className="impact-product__media impact-product__quick-shop-media"
                type="button"
                aria-label={`Quick shop ${product.name}`}
                onClick={() => onQuickShop(product)}
              >
                <img src={product.image} alt={product.alt} loading="lazy" />
                {product.id === 'pick-mix' && (
                  <img
                    className="impact-product__bucket-logo"
                    src="/brand/sub-zero-logo-primary.png"
                    alt=""
                    width="126"
                    height="54"
                    loading="lazy"
                    decoding="async"
                  />
                )}
                <span
                  className="impact-product__index"
                  data-index={String(index + 1).padStart(2, '0')}
                  aria-hidden="true"
                />
              </button>
              <div className="impact-product__copy">
                <div>
                  <h3>
                    <button
                      className="impact-product__title-button"
                      type="button"
                      aria-label={`View ${product.name} details`}
                      onClick={() => onQuickShop(product)}
                    >
                      {product.name}
                    </button>
                  </h3>
                  <strong>{formatCurrency(product.priceCents)}</strong>
                </div>
                <p>{product.description}</p>
                <button
                  className="impact-product__add"
                  type="button"
                  aria-label={`Add ${product.name} to cart`}
                  onClick={() => onAdd(product)}
                >
                  Add to cart &middot; {formatCurrency(product.priceCents)}
                  <Plus aria-hidden="true" />
                </button>
                {quantityInCart > 0 && (
                  <div className="impact-product__added">
                    <span>
                      <Check aria-hidden="true" />
                      {quantityInCart}{' '}
                      {quantityInCart === 1 ? 'item' : 'items'} in cart
                    </span>
                    <button type="button" onClick={onViewCart}>
                      View cart
                    </button>
                  </div>
                )}
              </div>
            </motion.article>
          )
        })}
      </div>
    </section>
  )
}

interface DeliveryCurtainCallProps {
  readonly selectedDelivery: DeliveryChoice | null
  readonly cartQuantity: number
  readonly onSelectDelivery: (choice: DeliveryChoice) => void
  readonly onReviewCart: () => void
  readonly deliveryLocked?: boolean
}

function formatDeliveryDate(date: Date) {
  return new Intl.DateTimeFormat('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Australia/Brisbane',
  }).format(date)
}

export function DeliveryCurtainCall({
  selectedDelivery,
  cartQuantity,
  onSelectDelivery,
  onReviewCart,
  deliveryLocked = false,
}: DeliveryCurtainCallProps) {
  const choices = useMemo(
    () =>
      PROPOSED_DELIVERY_SLOTS.map((slot) => {
        const [day, ...windowParts] = slot.label.split(',')
        return {
          id: slot.id,
          day,
          date: formatDeliveryDate(nextEligibleOccurrence(slot, new Date())),
          window: windowParts.join(',').trim(),
        } satisfies DeliveryChoice
      }),
    [],
  )

  return (
    <section
      className="impact-delivery"
      id="delivery-days"
      data-shop-section="delivery-curtain-call"
    >
      <div className="impact-delivery__copy">
        <p className="impact-kicker">The direct-delivery idea</p>
        <h2>We batch the joy. You pick the drop.</h2>
        <p>
          A proposed pilot groups nearby orders into fuller local runs instead
          of sending every repeat sale through a marketplace.
        </p>
        <div className="impact-delivery__route" aria-hidden="true">
          <span>Sub Zero</span>
          <i />
          <Truck />
          <i />
          <span>Your door</span>
        </div>
      </div>

      <div className="impact-delivery__planner">
        <DeliveryMap selectedDelivery={selectedDelivery} />
        <div className="impact-delivery__days">
          {choices.map((choice, index) => {
            const active = selectedDelivery?.id === choice.id
            return (
              <button
                key={choice.id}
                type="button"
                className={active ? 'is-active' : ''}
                aria-pressed={active}
                disabled={deliveryLocked}
                onClick={() => onSelectDelivery(choice)}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                <CalendarDays aria-hidden="true" />
                <strong>{choice.day}</strong>
                <small>{choice.window}</small>
                <b>{choice.date}</b>
                {active ? <Check aria-hidden="true" /> : <ArrowRight aria-hidden="true" />}
              </button>
            )
          })}
          {selectedDelivery && (
            <div className="impact-delivery__confirmation" role="status">
              <Check aria-hidden="true" />
              <span>
                <strong>{selectedDelivery.day} is held in this demo.</strong>
                <small>
                  {deliveryLocked
                    ? 'Locked to the Mystery Bucket plan in your cart.'
                    : 'No booking has been created.'}
                </small>
              </span>
              {cartQuantity > 0 ? (
                <button type="button" onClick={onReviewCart}>
                  Review my bucket
                  <ArrowRight aria-hidden="true" />
                </button>
              ) : (
                <a href="#create-a-bucket">
                  Build my bucket
                  <ArrowRight aria-hidden="true" />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      <p className="impact-delivery__fineprint">
        Sub Zero would choose the final days, zones, capacity and fee before launch.
      </p>
    </section>
  )
}

export function FounderTrust() {
  return (
    <section className="impact-trust" data-shop-section="founder-trust">
      <div className="impact-trust__stamp" aria-hidden="true">
        <Sparkles />
        <span>Local idea</span>
      </div>
      <div>
        <p className="impact-kicker">What happens behind the bucket</p>
        <h2>The customer gets theatre. The team gets a workable local run.</h2>
      </div>
      <div className="impact-trust__points">
        <p>
          <Sparkles aria-hidden="true" />
          <span>
            <strong>1. The weekly cut-off closes</strong>
            Customer tastes, exclusions, skips and pauses turn into one clear
            packing queue before Wednesday noon.
          </span>
        </p>
        <p>
          <ShieldCheck aria-hidden="true" />
          <span>
            <strong>2. Sub Zero packs to the guardrails</strong>
            The team keeps the surprise while respecting the selected bucket
            size, favourites and hard no&apos;s.
          </span>
        </p>
        <p>
          <Truck aria-hidden="true" />
          <span>
            <strong>3. Nearby drops become one route</strong>
            Weekly, fortnightly and one-off buckets join the selected Thursday,
            Friday or Saturday run instead of becoming isolated marketplace jobs.
          </span>
        </p>
        <a href="/direct-delivery-case">
          See the four-week business case
          <ArrowRight aria-hidden="true" />
        </a>
      </div>
    </section>
  )
}

export function TreatFaq() {
  const items = [
    {
      question: 'Is this a real Sub Zero ordering website?',
      answer:
        'No. It is a clickable proposal showing how direct ordering, bucket personalisation and scheduled delivery could feel.',
    },
    {
      question: 'How does the Mystery Bucket plan work?',
      answer:
        'Build a taste profile, choose one-off, weekly or fortnightly, select a proposed delivery run, review the exact per-drop total, then preview checkout and the skip, pause and cancel controls. This MVP creates no payment, order, subscription or delivery booking.',
    },
    {
      question: 'Are the local delivery days confirmed?',
      answer:
        'Not yet. The route, suburbs, order cut-offs, capacity and fee would be set with Sub Zero before a pilot.',
    },
  ]

  return (
    <section className="impact-faq" data-shop-section="faq">
      <p className="impact-kicker">The clear bits</p>
      <h2>A clear proposal, with no fine-print surprises.</h2>
      <div>
        {items.map((item, index) => (
          <details key={item.question} open={index === 0}>
            <summary>
              {item.question}
              <Plus aria-hidden="true" />
            </summary>
            <p>{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  )
}

interface CartReassuranceProps {
  readonly cartQuantity: number
  readonly cartTotal: number
  readonly onOpenCart: () => void
}

export function CartReassurance({
  cartQuantity,
  cartTotal,
  onOpenCart,
}: CartReassuranceProps) {
  return (
    <section className="impact-finale" data-shop-section="cart-reassurance">
      <div className="impact-finale__spray" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      <img
        className="impact-finale__bucket-logo"
        src="/brand/sub-zero-logo-primary.png"
        alt=""
        width="126"
        height="54"
        aria-hidden="true"
      />
      <p>Clickable proposal</p>
      <h2>
        Keep the joy close.
      </h2>
      {cartQuantity > 0 ? (
        <button type="button" onClick={onOpenCart}>
          View demo cart · {cartQuantity} {cartQuantity === 1 ? 'item' : 'items'} ·{' '}
          {formatCurrency(cartTotal)}
          <ArrowRight aria-hidden="true" />
        </button>
      ) : (
        <a href="#create-a-bucket">
          Create my bucket
          <ArrowRight aria-hidden="true" />
        </a>
      )}
    </section>
  )
}
